/**
 * QuotationImport — AI-powered competitor quotation import.
 *
 * Routes:
 *  • /admin-dashboard/quotations/import        – upload dialog
 *  • /admin-dashboard/quotations/import/:sid   – editable review screen
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Upload,
  Sparkles,
  ArrowLeft,
  Loader2,
  Plus,
  Trash2,
  CheckCircle2,
  AlertTriangle,
  ShieldCheck,
  RotateCcw,
  FileText,
  Search,
  X,
} from "lucide-react";
import { adminApi } from "./api";
import { formatINR, parseNumber } from "./format";

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp";
const MAX_MB = 25;

function newItemRow() {
  return {
    id: cryptoRandom(),
    competitor_name: "",
    competitor_description: "",
    quantity: 1,
    unit: "Nos",
    unit_price: 0,
    ref_line_total: 0,
    product_id: null,
    mapped_name: null,
    is_custom: true,
    confidence: { product_name: 1, description: 1, quantity: 1, unit_price: 1, catalog_match: 1, match_source: "manual" },
  };
}
function newRoomRow() {
  return { id: cryptoRandom(), name: "New Room", name_confidence: 1, items: [], ref_room_total: 0 };
}
function cryptoRandom() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return Math.random().toString(36).slice(2);
}

function confidenceTone(c) {
  if (c == null) return "gray";
  if (c >= 0.9) return "green";
  if (c >= 0.7) return "yellow";
  return "red";
}

function ConfidenceBadge({ value, label }) {
  if (value == null) return null;
  const tone = confidenceTone(value);
  const palette = {
    green: { bg: "rgba(34,197,94,0.12)", color: "#86efac", border: "rgba(34,197,94,0.35)" },
    yellow: { bg: "rgba(234,179,8,0.13)", color: "#fde68a", border: "rgba(234,179,8,0.4)" },
    red: { bg: "rgba(239,68,68,0.13)", color: "#fca5a5", border: "rgba(239,68,68,0.4)" },
    gray: { bg: "rgba(255,255,255,0.05)", color: "#9ca3af", border: "rgba(255,255,255,0.1)" },
  }[tone];
  return (
    <span
      title={label || `Confidence: ${(value * 100).toFixed(0)}%`}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9.5px] font-bold tracking-wide ax-mono"
      style={{ background: palette.bg, color: palette.color, border: `1px solid ${palette.border}` }}
    >
      {(value * 100).toFixed(0)}%
    </span>
  );
}

function computeRunningTotals(rooms, gstPct, discountPct, install) {
  let subtotal = 0;
  for (const r of rooms || []) {
    for (const it of r.items || []) {
      subtotal += parseNumber(it.quantity) * parseNumber(it.unit_price);
    }
  }
  const disc = +(subtotal * (parseNumber(discountPct) / 100)).toFixed(2);
  const afterDisc = subtotal - disc;
  const taxable = afterDisc + parseNumber(install);
  const gst = +(taxable * (parseNumber(gstPct) / 100)).toFixed(2);
  const grand = +(taxable + gst).toFixed(2);
  return {
    subtotal: +subtotal.toFixed(2),
    discount: disc,
    taxable: +taxable.toFixed(2),
    gst,
    grand,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Upload screen
// ─────────────────────────────────────────────────────────────────────────────

export function QuotationImportUpload() {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const handleFiles = useCallback(
    async (files) => {
      const f = files?.[0];
      if (!f) return;
      const sizeMb = f.size / (1024 * 1024);
      if (sizeMb > MAX_MB) {
        toast.error(`File is ${sizeMb.toFixed(1)} MB. Limit is ${MAX_MB} MB.`);
        return;
      }
      const fd = new FormData();
      fd.append("file", f);
      setBusy(true);
      try {
        // Upload returns immediately with status="processing" (202). The
        // actual AI extraction runs in a backend background task; the review
        // screen polls until it flips to "review" or "failed".
        const r = await adminApi.post("/quotations/import", fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60000, // upload itself should be fast
        });
        const sid = r.data?.session_id;
        if (!sid) throw new Error("No session id returned.");
        toast.success("Uploaded — extracting now…");
        nav(`/admin-dashboard/quotations/import/${sid}`, { replace: true });
      } catch (e) {
        toast.error(e?.response?.data?.detail || e.message || "Upload failed.");
        setBusy(false);
      }
    },
    [nav]
  );

  return (
    <div className="p-10 max-w-[920px] mx-auto" data-testid="quotation-import-upload">
      <header className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-3.5 h-3.5" style={{ color: "var(--ax-gold)" }} />
            <span className="text-[10.5px] tracking-[0.32em] uppercase" style={{ color: "var(--ax-gold)" }}>
              Quotation Migration Assistant
            </span>
          </div>
          <h1 className="ax-serif text-5xl tracking-[-0.02em]">Import existing quotation</h1>
          <p className="text-sm mt-3 max-w-[640px]" style={{ color: "var(--ax-text-dim)" }}>
            Upload a PDF or image of any competitor&apos;s quotation. AI extracts every room, product,
            and price — then maps them to the GenieX catalog. You review and confirm. Our engine
            always recomputes the final totals.
          </p>
        </div>
        <button
          type="button"
          className="ax-btn ax-btn-ghost"
          onClick={() => nav("/admin-dashboard/quotations")}
          data-testid="qi-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back to quotations
        </button>
      </header>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          if (!busy) handleFiles(e.dataTransfer.files);
        }}
        className="ax-glass p-12 text-center cursor-pointer transition-all"
        style={{
          borderColor: drag ? "var(--ax-gold)" : undefined,
          borderStyle: "dashed",
          borderWidth: 1.5,
          background: drag ? "rgba(212,175,106,0.05)" : undefined,
        }}
        onClick={() => !busy && inputRef.current?.click()}
        data-testid="qi-dropzone"
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          hidden
          onChange={(e) => handleFiles(e.target.files)}
          data-testid="qi-file-input"
        />
        {busy ? (
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="w-12 h-12 animate-spin" style={{ color: "var(--ax-gold)" }} />
            <div>
              <div className="ax-serif text-2xl mb-1">Reading your document…</div>
              <div className="text-sm" style={{ color: "var(--ax-text-dim)" }}>
                AI is extracting rooms, products and prices. This takes 20-60 seconds.
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4">
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center"
              style={{ background: "rgba(212,175,106,0.1)", border: "1px solid var(--ax-gold)" }}
            >
              <Upload className="w-9 h-9" style={{ color: "var(--ax-gold)" }} />
            </div>
            <div>
              <div className="ax-serif text-2xl mb-1">Drop a PDF or image here</div>
              <div className="text-sm" style={{ color: "var(--ax-text-dim)" }}>
                or click to browse — accepts PDF, PNG, JPG, JPEG, WEBP (up to {MAX_MB} MB)
              </div>
            </div>
            <button type="button" className="ax-btn ax-btn-gold mt-2" data-testid="qi-browse">
              <Upload className="w-4 h-4" /> Choose file
            </button>
          </div>
        )}
      </div>

      <div className="mt-8 grid md:grid-cols-3 gap-4">
        <FeatureChip icon={Sparkles} title="OCR + AI vision" body="Scanned PDFs, photos, native PDFs — all handled automatically." />
        <FeatureChip icon={ShieldCheck} title="Engine-truthed math" body="Every total is recomputed by GenieX. Uploaded numbers can be wrong; ours never are." />
        <FeatureChip icon={RotateCcw} title="Learns your mappings" body="Mark a custom item once → future imports auto-map it instantly." />
      </div>
    </div>
  );
}

function FeatureChip({ icon: Icon, title, body }) {
  return (
    <div className="ax-glass p-5">
      <Icon className="w-5 h-5 mb-3" style={{ color: "var(--ax-gold)" }} />
      <div className="text-sm font-semibold mb-1">{title}</div>
      <div className="text-xs" style={{ color: "var(--ax-text-dim)" }}>
        {body}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Review screen
// ─────────────────────────────────────────────────────────────────────────────

export function QuotationImportReview() {
  const { sid } = useParams();
  const nav = useNavigate();
  const [session, setSession] = useState(null);
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [products, setProducts] = useState([]);
  const [discountPct, setDiscountPct] = useState(0);
  const [install, setInstall] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let pollTimer = null;

    async function loadOnce(includeProducts) {
      const calls = [adminApi.get(`/quotations/import/${sid}`)];
      if (includeProducts) calls.push(adminApi.get("/products"));
      const [sess, prods] = await Promise.all(calls);
      if (cancelled) return null;
      setSession(sess.data);
      if (sess.data.review) setReview(sess.data.review);
      if (prods) setProducts(prods.data?.products || []);
      return sess.data;
    }

    (async () => {
      try {
        const data = await loadOnce(true);
        if (!data) return;
        if (data.status === "processing") {
          // Poll every 4 seconds. Stop on cancel or terminal status.
          const tick = async () => {
            try {
              const d = await loadOnce(false);
              if (!d) return;
              if (d.status === "processing") {
                pollTimer = setTimeout(tick, 4000);
              } else if (d.status === "failed") {
                toast.error(d.error || "AI extraction failed.");
              } else {
                toast.success("Extraction complete — review below.");
              }
            } catch (e) {
              if (!cancelled) toast.error(e?.response?.data?.detail || "Polling failed.");
            }
          };
          pollTimer = setTimeout(tick, 4000);
        } else if (data.status === "failed") {
          toast.error(data.error || "AI extraction failed.");
        }
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load import session.");
        nav("/admin-dashboard/quotations", { replace: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, [sid, nav]);

  const totals = useMemo(
    () =>
      review
        ? computeRunningTotals(review.rooms, review.reference_totals?.gst_pct ?? 18, discountPct, install)
        : null,
    [review, discountPct, install]
  );

  const updateClient = (patch) =>
    setReview((r) => ({ ...r, client: { ...r.client, ...patch } }));
  const updateRoom = (rid, patch) =>
    setReview((r) => ({
      ...r,
      rooms: r.rooms.map((rm) => (rm.id === rid ? { ...rm, ...patch } : rm)),
    }));
  const updateItem = (rid, iid, patch) =>
    setReview((r) => ({
      ...r,
      rooms: r.rooms.map((rm) =>
        rm.id === rid
          ? { ...rm, items: rm.items.map((it) => (it.id === iid ? { ...it, ...patch } : it)) }
          : rm
      ),
    }));
  const removeRoom = (rid) =>
    setReview((r) => ({ ...r, rooms: r.rooms.filter((rm) => rm.id !== rid) }));
  const removeItem = (rid, iid) =>
    setReview((r) => ({
      ...r,
      rooms: r.rooms.map((rm) =>
        rm.id === rid ? { ...rm, items: rm.items.filter((it) => it.id !== iid) } : rm
      ),
    }));
  const addRoom = () =>
    setReview((r) => ({ ...r, rooms: [...r.rooms, newRoomRow()] }));
  const addItem = (rid) =>
    setReview((r) => ({
      ...r,
      rooms: r.rooms.map((rm) =>
        rm.id === rid ? { ...rm, items: [...rm.items, newItemRow()] } : rm
      ),
    }));

  const saveDraft = async () => {
    if (!review) return;
    setSaving(true);
    try {
      await adminApi.put(`/quotations/import/${sid}`, { review });
      toast.success("Draft saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const confirmGenerate = async () => {
    if (!review) return;
    if (!review.rooms?.length) {
      toast.error("Add at least one room before generating.");
      return;
    }
    setConfirming(true);
    try {
      // Persist latest edits, then confirm.
      await adminApi.put(`/quotations/import/${sid}`, { review });
      const r = await adminApi.post(`/quotations/import/${sid}/confirm`, { review });
      const qid = r.data?.quotation_id;
      toast.success(`Quotation ${r.data?.quote_number || ""} generated.`);
      nav(`/admin-dashboard/quotations/${qid}`, { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate quotation.");
      setConfirming(false);
    }
  };

  if (loading) {
    return (
      <div className="p-10 flex items-center justify-center gap-3" data-testid="qi-review-loading">
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: "var(--ax-gold)" }} />
        <span style={{ color: "var(--ax-text-dim)" }}>Loading import session…</span>
      </div>
    );
  }

  if (session?.status === "processing") {
    return (
      <div
        className="p-10 max-w-[640px] mx-auto text-center"
        data-testid="qi-review-processing"
      >
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6"
             style={{ background: "rgba(201,169,97,0.08)", border: "1px solid rgba(201,169,97,0.25)" }}>
          <Loader2 className="w-7 h-7 animate-spin" style={{ color: "var(--ax-gold)" }} />
        </div>
        <div className="flex items-center justify-center gap-2 mb-3">
          <Sparkles className="w-3.5 h-3.5" style={{ color: "var(--ax-gold)" }} />
          <span className="text-[10.5px] tracking-[0.32em] uppercase" style={{ color: "var(--ax-gold)" }}>
            AI Extraction
          </span>
        </div>
        <h2 className="ax-serif text-3xl tracking-[-0.02em] mb-3">Reading your quotation…</h2>
        <p className="text-sm leading-relaxed" style={{ color: "var(--ax-text-dim)" }}>
          GenieX is parsing <span className="ax-mono">{session?.file_name || "your file"}</span>,
          mapping items to your catalog and recalculating totals. Complex documents
          can take <span className="text-white">60–180 seconds</span>. This page will
          refresh automatically when it's ready — please keep this tab open.
        </p>
      </div>
    );
  }

  if (session?.status === "failed") {
    return (
      <div
        className="p-10 max-w-[640px] mx-auto text-center"
        data-testid="qi-review-failed"
      >
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6"
             style={{ background: "rgba(220, 38, 38, 0.08)", border: "1px solid rgba(220, 38, 38, 0.3)" }}>
          <span className="text-2xl" style={{ color: "rgb(248, 113, 113)" }}>!</span>
        </div>
        <h2 className="ax-serif text-3xl tracking-[-0.02em] mb-3">Extraction failed</h2>
        <p className="text-sm leading-relaxed mb-6" style={{ color: "var(--ax-text-dim)" }}>
          {session?.error || "We couldn't process this file. Please try a clearer scan or a different format."}
        </p>
        <button
          type="button"
          className="ax-btn ax-btn-primary"
          onClick={() => nav("/admin-dashboard/quotations/import", { replace: true })}
          data-testid="qi-failed-retry"
        >
          Try another upload
        </button>
      </div>
    );
  }

  if (!review) {
    return (
      <div className="p-10 flex items-center justify-center gap-3" data-testid="qi-review-empty">
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: "var(--ax-gold)" }} />
        <span style={{ color: "var(--ax-text-dim)" }}>Preparing review…</span>
      </div>
    );
  }

  const refTotals = review.reference_totals || {};
  const totalLowConf = review.rooms.reduce(
    (acc, r) =>
      acc +
      r.items.reduce((a, it) => {
        const conf = it.confidence || {};
        const min = Math.min(conf.product_name ?? 1, conf.unit_price ?? 1, conf.quantity ?? 1);
        return min < 0.7 ? a + 1 : a;
      }, 0),
    0
  );
  const totalUnmapped = review.rooms.reduce(
    (a, r) => a + r.items.filter((it) => it.is_custom).length,
    0
  );

  return (
    <div className="p-8 max-w-[1320px] mx-auto pb-32" data-testid="qi-review">
      <header className="mb-8 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-3.5 h-3.5" style={{ color: "var(--ax-gold)" }} />
            <span className="text-[10.5px] tracking-[0.32em] uppercase" style={{ color: "var(--ax-gold)" }}>
              Review & Refine
            </span>
          </div>
          <h1 className="ax-serif text-4xl tracking-[-0.02em]">Imported quotation</h1>
          <div className="text-sm mt-2" style={{ color: "var(--ax-text-dim)" }}>
            Source: <span className="ax-mono">{session?.file_name || "—"}</span> &nbsp;·&nbsp; AI
            extracted{" "}
            <span className="ax-mono">
              {review.rooms.reduce((a, r) => a + r.items.length, 0)}
            </span>{" "}
            items across <span className="ax-mono">{review.rooms.length}</span> rooms
          </div>
        </div>
        <button
          type="button"
          className="ax-btn ax-btn-ghost"
          onClick={() => nav("/admin-dashboard/quotations/import")}
          data-testid="qi-back"
        >
          <ArrowLeft className="w-4 h-4" /> New import
        </button>
      </header>

      {/* Smart alerts */}
      <div className="grid md:grid-cols-2 gap-3 mb-6">
        {totalLowConf > 0 && (
          <div
            className="ax-glass p-4 flex items-center gap-3"
            style={{ borderColor: "rgba(234,179,8,0.35)" }}
            data-testid="qi-alert-lowconf"
          >
            <AlertTriangle className="w-5 h-5" style={{ color: "#fde68a" }} />
            <div className="text-sm">
              <span className="font-semibold">{totalLowConf}</span> field{totalLowConf > 1 ? "s" : ""}{" "}
              flagged for review.{" "}
              <span style={{ color: "var(--ax-text-dim)" }}>Verify highlighted cells below.</span>
            </div>
          </div>
        )}
        {totalUnmapped > 0 && (
          <div
            className="ax-glass p-4 flex items-center gap-3"
            style={{ borderColor: "rgba(239,68,68,0.35)" }}
            data-testid="qi-alert-unmapped"
          >
            <FileText className="w-5 h-5" style={{ color: "#fca5a5" }} />
            <div className="text-sm">
              <span className="font-semibold">{totalUnmapped}</span> product{totalUnmapped > 1 ? "s" : ""}{" "}
              kept as custom (no confident GenieX match).
              <span style={{ color: "var(--ax-text-dim)" }}> Map them now to remember next time.</span>
            </div>
          </div>
        )}
      </div>

      {/* Client */}
      <section className="ax-glass p-6 mb-6" data-testid="qi-client">
        <div className="flex items-center justify-between mb-4">
          <h3 className="ax-serif text-xl">Client Details</h3>
          <div className="text-[10px] tracking-[0.3em] uppercase" style={{ color: "var(--ax-text-muted)" }}>
            Extracted from document
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            ["name", "Client Name"],
            ["phone", "Phone"],
            ["email", "Email"],
            ["address", "Address"],
            ["project_name", "Project Name"],
            ["project_type", "Project Type"],
            ["valid_until", "Valid Until (YYYY-MM-DD)"],
          ].map(([k, label]) => {
            const conf = review.client.confidence?.[k];
            return (
              <label key={k} className="block" data-testid={`qi-client-${k}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] uppercase tracking-wider" style={{ color: "var(--ax-text-muted)" }}>
                    {label}
                  </span>
                  <ConfidenceBadge value={conf} />
                </div>
                <input
                  className="ax-input"
                  style={
                    conf != null && conf < 0.7
                      ? { borderColor: "rgba(239,68,68,0.45)", background: "rgba(239,68,68,0.06)" }
                      : undefined
                  }
                  value={review.client[k] || ""}
                  onChange={(e) => updateClient({ [k]: e.target.value })}
                />
              </label>
            );
          })}
        </div>
      </section>

      {/* Rooms */}
      <section className="space-y-4 mb-6">
        {review.rooms.map((room) => (
          <RoomCard
            key={room.id}
            room={room}
            products={products}
            onChange={(patch) => updateRoom(room.id, patch)}
            onItemChange={(iid, patch) => updateItem(room.id, iid, patch)}
            onAddItem={() => addItem(room.id)}
            onRemoveItem={(iid) => removeItem(room.id, iid)}
            onRemove={() => removeRoom(room.id)}
          />
        ))}
        <button
          type="button"
          className="ax-btn ax-btn-ghost w-full"
          onClick={addRoom}
          data-testid="qi-add-room"
        >
          <Plus className="w-4 h-4" /> Add room
        </button>
      </section>

      {/* Totals diff strip */}
      <section className="ax-glass p-6 mb-6" data-testid="qi-totals">
        <div className="flex items-center justify-between mb-4">
          <h3 className="ax-serif text-xl">Totals</h3>
          <span className="text-[10.5px] tracking-[0.3em] uppercase flex items-center gap-1.5" style={{ color: "var(--ax-gold)" }}>
            <ShieldCheck className="w-3 h-3" /> Engine-calculated
          </span>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <div className="text-[10px] tracking-[0.32em] uppercase mb-3" style={{ color: "var(--ax-text-muted)" }}>
              Uploaded (reference only)
            </div>
            <div className="space-y-1.5 text-sm">
              <RefRow label="Subtotal" v={refTotals.subtotal} />
              <RefRow label="Discount" v={refTotals.discount} />
              <RefRow label="GST %" v={refTotals.gst_pct} suffix="%" raw />
              <RefRow label="Grand Total" v={refTotals.grand_total} bold />
            </div>
          </div>

          <div>
            <div className="text-[10px] tracking-[0.32em] uppercase mb-3" style={{ color: "var(--ax-gold)" }}>
              GenieX Recompute
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <label className="block">
                <div className="text-[11px] uppercase tracking-wider mb-1" style={{ color: "var(--ax-text-muted)" }}>
                  Discount %
                </div>
                <input
                  className="ax-input"
                  type="number"
                  value={discountPct}
                  onChange={(e) => setDiscountPct(parseNumber(e.target.value))}
                  data-testid="qi-discount"
                />
              </label>
              <label className="block">
                <div className="text-[11px] uppercase tracking-wider mb-1" style={{ color: "var(--ax-text-muted)" }}>
                  Installation ₹
                </div>
                <input
                  className="ax-input"
                  type="number"
                  value={install}
                  onChange={(e) => setInstall(parseNumber(e.target.value))}
                  data-testid="qi-install"
                />
              </label>
            </div>
            <div className="space-y-1.5 text-sm">
              <CalcRow label="Subtotal" v={totals?.subtotal} />
              <CalcRow label="Discount" v={totals?.discount} />
              <CalcRow label={`GST (${review.reference_totals?.gst_pct ?? 18}%)`} v={totals?.gst} />
              <CalcRow label="Grand Total" v={totals?.grand} bold />
            </div>
          </div>
        </div>
      </section>

      {/* Sticky action bar */}
      <div
        className="fixed bottom-0 left-0 right-0 backdrop-blur-md"
        style={{ background: "rgba(10,8,4,0.92)", borderTop: "1px solid var(--ax-border)", zIndex: 40 }}
      >
        <div className="max-w-[1320px] mx-auto px-8 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="text-sm flex items-center gap-3">
            <ShieldCheck className="w-4 h-4" style={{ color: "var(--ax-gold)" }} />
            <span>
              Generating:{" "}
              <span className="ax-mono font-bold" style={{ color: "var(--ax-gold)" }}>
                {formatINR(totals?.grand || 0)}
              </span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="ax-btn ax-btn-ghost"
              onClick={saveDraft}
              disabled={saving}
              data-testid="qi-save-draft"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              Save draft
            </button>
            <button
              type="button"
              className="ax-btn ax-btn-gold"
              onClick={confirmGenerate}
              disabled={confirming}
              data-testid="qi-confirm"
            >
              {confirming ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4" />
              )}
              Confirm & generate
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function RefRow({ label, v, suffix, bold, raw }) {
  return (
    <div className="flex items-center justify-between">
      <span style={{ color: "var(--ax-text-muted)" }}>{label}</span>
      <span
        className={bold ? "ax-mono font-bold" : "ax-mono"}
        style={{ color: bold ? "#ffffff" : "var(--ax-text-dim)" }}
      >
        {raw ? `${v ?? 0}${suffix || ""}` : formatINR(v || 0)}
      </span>
    </div>
  );
}
function CalcRow({ label, v, bold }) {
  return (
    <div className="flex items-center justify-between">
      <span style={{ color: "var(--ax-text-muted)" }}>{label}</span>
      <span
        className={bold ? "ax-mono font-bold" : "ax-mono"}
        style={{ color: bold ? "var(--ax-gold)" : "#ffffff", fontSize: bold ? "1.1em" : undefined }}
      >
        {formatINR(v || 0)}
      </span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Room card
// ─────────────────────────────────────────────────────────────────────────────

function RoomCard({ room, products, onChange, onItemChange, onAddItem, onRemoveItem, onRemove }) {
  const roomTotal = useMemo(
    () =>
      (room.items || []).reduce(
        (a, it) => a + parseNumber(it.quantity) * parseNumber(it.unit_price),
        0
      ),
    [room.items]
  );

  return (
    <div className="ax-glass" data-testid={`qi-room-${room.id}`}>
      <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: "var(--ax-border)" }}>
        <div className="flex items-center gap-3 flex-1">
          <ConfidenceBadge value={room.name_confidence} label="Room name confidence" />
          <input
            className="ax-input flex-1"
            value={room.name}
            onChange={(e) => onChange({ name: e.target.value })}
            data-testid={`qi-room-name-${room.id}`}
          />
        </div>
        <div className="flex items-center gap-3 ml-4">
          <div className="text-right">
            <div className="text-[10px] tracking-[0.32em] uppercase" style={{ color: "var(--ax-text-muted)" }}>
              Room total
            </div>
            <div className="ax-mono text-base" style={{ color: "var(--ax-gold)" }}>
              {formatINR(roomTotal)}
            </div>
          </div>
          <button
            type="button"
            className="ax-btn ax-btn-ghost ax-btn-danger px-2"
            onClick={onRemove}
            title="Remove room"
            data-testid={`qi-room-remove-${room.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="ax-scroll overflow-x-auto">
        <table className="ax-table" style={{ minWidth: 1080 }}>
          <thead>
            <tr>
              <th style={{ width: 270 }}>Product</th>
              <th style={{ width: 260 }}>GenieX mapping</th>
              <th style={{ width: 220 }}>Description</th>
              <th style={{ width: 70 }}>Qty</th>
              <th style={{ width: 80 }}>Unit</th>
              <th style={{ width: 110 }}>Unit ₹</th>
              <th style={{ width: 110 }}>Line ₹</th>
              <th style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {room.items.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center py-6 text-sm" style={{ color: "var(--ax-text-muted)" }}>
                  No items in this room.
                </td>
              </tr>
            )}
            {room.items.map((it) => (
              <ItemRow
                key={it.id}
                item={it}
                products={products}
                onChange={(patch) => onItemChange(it.id, patch)}
                onRemove={() => onRemoveItem(it.id)}
              />
            ))}
          </tbody>
        </table>
      </div>
      <div className="p-3 border-t flex" style={{ borderColor: "var(--ax-border)" }}>
        <button
          type="button"
          className="ax-btn ax-btn-ghost"
          onClick={onAddItem}
          data-testid={`qi-room-add-item-${room.id}`}
        >
          <Plus className="w-3.5 h-3.5" /> Add item
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Item row (with product mapper)
// ─────────────────────────────────────────────────────────────────────────────

function ItemRow({ item, products, onChange, onRemove }) {
  const conf = item.confidence || {};
  const lineTotal = parseNumber(item.quantity) * parseNumber(item.unit_price);

  return (
    <tr data-testid={`qi-item-${item.id}`}>
      <td>
        <div className="flex items-start gap-1.5">
          <ConfidenceBadge value={conf.product_name} label="Product name confidence" />
          <input
            className="ax-input"
            style={
              (conf.product_name ?? 1) < 0.7
                ? { borderColor: "rgba(239,68,68,0.45)", background: "rgba(239,68,68,0.06)" }
                : undefined
            }
            value={item.competitor_name || ""}
            onChange={(e) => onChange({ competitor_name: e.target.value })}
          />
        </div>
        {item.is_custom && (
          <span
            className="inline-flex items-center gap-1 mt-1.5 px-1.5 py-0.5 rounded text-[9.5px] font-bold tracking-wide"
            style={{
              background: "rgba(239,68,68,0.13)",
              color: "#fca5a5",
              border: "1px solid rgba(239,68,68,0.4)",
            }}
          >
            CUSTOM — not in catalog
          </span>
        )}
      </td>
      <td>
        <ProductPicker
          products={products}
          selectedId={item.product_id}
          selectedName={item.mapped_name}
          confidence={conf.catalog_match}
          source={conf.match_source}
          onSelect={(p) =>
            onChange({
              product_id: p?.id || null,
              mapped_name: p?.name || null,
              is_custom: !p,
              confidence: { ...conf, catalog_match: p ? 1 : 0, match_source: "manual" },
            })
          }
        />
      </td>
      <td>
        <input
          className="ax-input"
          value={item.competitor_description || ""}
          onChange={(e) => onChange({ competitor_description: e.target.value })}
        />
      </td>
      <td>
        <input
          className="ax-input"
          type="number"
          step="0.5"
          value={item.quantity}
          onChange={(e) => onChange({ quantity: parseNumber(e.target.value) })}
        />
      </td>
      <td>
        <input
          className="ax-input"
          value={item.unit || "Nos"}
          onChange={(e) => onChange({ unit: e.target.value })}
        />
      </td>
      <td>
        <input
          className="ax-input"
          type="number"
          value={item.unit_price}
          onChange={(e) => onChange({ unit_price: parseNumber(e.target.value) })}
        />
      </td>
      <td className="ax-mono text-sm" style={{ color: "var(--ax-gold)" }}>
        {formatINR(lineTotal)}
      </td>
      <td>
        <button
          type="button"
          className="ax-btn ax-btn-ghost ax-btn-danger px-2"
          onClick={onRemove}
          title="Remove"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </td>
    </tr>
  );
}

function ProductPicker({ products, selectedId, selectedName, confidence, source, onSelect }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return products.slice(0, 30);
    return products.filter((p) => (p.name || "").toLowerCase().includes(term)).slice(0, 40);
  }, [q, products]);

  const tone = selectedId
    ? source === "memory"
      ? "memory"
      : "matched"
    : "unmatched";
  const tonePalette = {
    matched: { bg: "rgba(34,197,94,0.07)", border: "rgba(34,197,94,0.3)", color: "#86efac" },
    memory: { bg: "rgba(212,175,106,0.08)", border: "rgba(212,175,106,0.35)", color: "#f4d57f" },
    unmatched: { bg: "rgba(239,68,68,0.06)", border: "rgba(239,68,68,0.3)", color: "#fca5a5" },
  }[tone];

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        className="w-full px-2.5 py-1.5 rounded text-left flex items-center justify-between gap-2 text-xs"
        style={{ background: tonePalette.bg, border: `1px solid ${tonePalette.border}`, color: "#ffffff" }}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="truncate">
          {selectedId ? (
            <>
              <span style={{ color: tonePalette.color }}>●</span> {selectedName || "Mapped"}
            </>
          ) : (
            <span style={{ color: tonePalette.color }}>Map to catalog…</span>
          )}
        </span>
        <div className="flex items-center gap-1.5 shrink-0">
          {confidence != null && <ConfidenceBadge value={confidence} />}
          {source === "memory" && (
            <span
              className="text-[8.5px] tracking-wider"
              style={{ color: "#f4d57f" }}
              title="Auto-applied from learned mapping memory"
            >
              MEM
            </span>
          )}
        </div>
      </button>
      {open && (
        <div
          className="absolute z-50 mt-1 left-0 right-0 ax-glass p-2 shadow-xl"
          style={{ minWidth: 280, maxHeight: 320 }}
        >
          <div className="flex items-center gap-2 mb-2 px-2 py-1.5 rounded" style={{ background: "rgba(255,255,255,0.03)" }}>
            <Search className="w-3.5 h-3.5" style={{ color: "var(--ax-text-muted)" }} />
            <input
              autoFocus
              className="bg-transparent flex-1 outline-none text-xs"
              placeholder="Search GenieX catalog…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            {selectedId && (
              <button
                type="button"
                onClick={() => {
                  onSelect(null);
                  setOpen(false);
                }}
                className="text-[10px] tracking-wider px-1.5 py-0.5 rounded"
                style={{ color: "#fca5a5", border: "1px solid rgba(239,68,68,0.3)" }}
              >
                <X className="w-2.5 h-2.5 inline" /> Unmap
              </button>
            )}
          </div>
          <div className="overflow-y-auto" style={{ maxHeight: 250 }}>
            {filtered.length === 0 && (
              <div className="text-center py-4 text-[11px]" style={{ color: "var(--ax-text-muted)" }}>
                No products match.
              </div>
            )}
            {filtered.map((p) => (
              <button
                key={p.id}
                type="button"
                className="w-full text-left px-2 py-1.5 rounded text-xs hover:bg-white/5"
                onClick={() => {
                  onSelect(p);
                  setOpen(false);
                  setQ("");
                }}
              >
                <div className="truncate">{p.name}</div>
                <div className="text-[10px] truncate" style={{ color: "var(--ax-text-muted)" }}>
                  {p.category || ""} {p.brand ? `· ${p.brand}` : ""}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
